/**
 * ActivationGate.js — Module 1 · Universal Institutional Activation Gate
 *
 * Public route: /activate
 * 4-step flow:
 *   1. Entity picker (7 types)
 *   2. Orientation sandbox preview (read-only)
 *   3. Mutual Service Activation Protocol (6 clauses) + entity + signer form
 *   4. Success — Activation Certificate + Vault Hash
 *
 * Terminology discipline: "digital platform" · never "AI" · never "autonomous"
 * Viewport: 100dvh dynamic scaling
 */
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NAVY = "#020A1F";
const CYAN = "#00CFCF";
const CHROME = "#A0A0A0";
const INK = "#F5F5F7";

const shell = {
  minHeight: "100dvh",
  background: NAVY,
  color: INK,
  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  padding: "clamp(16px, 4vw, 48px)",
  boxSizing: "border-box",
};
const card = {
  background: "rgba(255,255,255,0.03)",
  border: `1px solid ${CHROME}33`,
  borderRadius: 12,
  padding: "clamp(16px, 3vw, 28px)",
  marginBottom: 20,
};
const btnPrimary = {
  background: CYAN, color: NAVY, border: 0, padding: "12px 24px",
  fontWeight: 700, borderRadius: 8, cursor: "pointer", letterSpacing: "0.03em",
  textTransform: "uppercase", fontSize: 13,
};
const btnGhost = {
  background: "transparent", color: INK, border: `1px solid ${CHROME}55`,
  padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontSize: 13,
};
const input = {
  width: "100%", background: "rgba(0,0,0,0.35)", border: `1px solid ${CHROME}44`,
  color: INK, padding: "10px 12px", borderRadius: 6, fontSize: 14,
  fontFamily: "inherit", boxSizing: "border-box",
};

export default function ActivationGate() {
  const location = useLocation();
  const preselect = new URLSearchParams(location.search).get("entity_type") || null;
  const [step, setStep] = useState(1);
  const [entityTypes, setEntityTypes] = useState([]);
  const [protocol, setProtocol] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [form, setForm] = useState({
    entity_legal_name: "", entity_ein_or_tax_id: "",
    signer_full_name: "", signer_title: "", signer_email: "", signer_phone: "",
  });
  const [consent, setConsent] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [et, pr] = await Promise.all([
          fetch(`${BACKEND_URL}/api/activation/entity-types`).then(r => r.json()),
          fetch(`${BACKEND_URL}/api/activation/protocol`).then(r => r.json()),
        ]);
        setEntityTypes(et.entity_types || []);
        setProtocol(pr);
        // Booth QR pre-select: auto-jump to orientation with matched tile.
        if (preselect) {
          const match = (et.entity_types || []).find(x => x.id === preselect);
          if (match) { setSelectedType(match); setStep(2); }
        }
      } catch (e) { setError("Digital platform unreachable. Try again shortly."); }
    })();
  }, [preselect]);

  const allClausesAccepted = protocol && protocol.clauses.every(c => consent[c.id]);

  const submit = async () => {
    if (!selectedType || !allClausesAccepted) return;
    setSubmitting(true); setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/activation/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entity_type: selectedType.id,
          ...form,
          consent_accepted_clauses: Object.keys(consent).filter(k => consent[k]),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Activation failed");
      setResult(data);
      setStep(4);
    } catch (e) { setError(e.message); }
    setSubmitting(false);
  };

  return (
    <div style={shell} data-testid="activation-gate-shell">
      {/* Header */}
      <div style={{ ...card, borderColor: `${CYAN}44`, background: "rgba(0,207,207,0.05)" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 6 }}>
          MOOVIN FORWARD LLC · ROI STABILIZATION FIRM · DFW METROPLEX
        </div>
        <h1 style={{ margin: 0, fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 800 }}>
          Universal Institutional Activation Gate
        </h1>
        <div style={{ marginTop: 8, color: CHROME, fontSize: 14, lineHeight: 1.5 }}>
          Digital platform onboarding for property owners, receivers, lenders, asset managers,
          trade contractors, and vendors. Read-only orientation, then a court-grade activation
          certificate under ESIGN · UETA · Texas UETA.
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap", fontSize: 11 }} data-testid="activation-step-tracker">
          {["1 · Entity", "2 · Orientation", "3 · Protocol + Signature", "4 · Certificate"].map((label, i) => (
            <span key={i} style={{
              padding: "4px 10px", borderRadius: 12,
              background: step === i + 1 ? CYAN : "rgba(255,255,255,0.06)",
              color: step === i + 1 ? NAVY : CHROME,
              fontWeight: step === i + 1 ? 700 : 500,
            }}>{label}</span>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ ...card, borderColor: "#f04040", background: "rgba(240,64,64,0.1)" }} data-testid="activation-error">
          {error}
        </div>
      )}

      {/* Step 1 · Entity picker */}
      {step === 1 && (
        <div style={card} data-testid="activation-step-1">
          <h2 style={{ marginTop: 0 }}>Select your entity type</h2>
          <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
            {entityTypes.map(et => (
              <button key={et.id}
                onClick={() => { setSelectedType(et); setStep(2); }}
                data-testid={`entity-type-${et.id}`}
                style={{
                  ...card, marginBottom: 0, textAlign: "left", cursor: "pointer",
                  background: "rgba(255,255,255,0.05)",
                  borderColor: selectedType?.id === et.id ? CYAN : `${CHROME}33`,
                }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{et.label}</div>
                <div style={{ fontSize: 12, color: CHROME, lineHeight: 1.4 }}>{et.description}</div>
                <div style={{ marginTop: 8, fontSize: 10, letterSpacing: "0.1em", color: CYAN }}>
                  TIER {et.mandate_tier} · POST-ACTIVATION ROLE: {et.post_activation_role}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2 · Orientation */}
      {step === 2 && selectedType && (
        <div style={card} data-testid="activation-step-2">
          <h2 style={{ marginTop: 0 }}>Read-only orientation · {selectedType.label}</h2>
          <div style={{ color: CHROME, marginBottom: 12, fontSize: 14 }}>
            Executive-grade sandbox preview. No binding duties, financial obligations, or performance
            covenants attach until you execute the Mutual Service Activation Protocol.
          </div>
          <ul style={{ paddingLeft: 20 }}>
            {selectedType.sandbox_preview.map((p, i) => (
              <li key={i} style={{ marginBottom: 6 }}>{p}</li>
            ))}
          </ul>
          <div style={{ marginTop: 20, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button style={btnGhost} onClick={() => setStep(1)} data-testid="activation-back-1">← Change entity</button>
            <button style={btnPrimary} onClick={() => setStep(3)} data-testid="activation-continue-3">
              Continue to protocol →
            </button>
          </div>
        </div>
      )}

      {/* Step 3 · Protocol + form */}
      {step === 3 && selectedType && protocol && (
        <div data-testid="activation-step-3">
          <div style={card}>
            <h2 style={{ marginTop: 0 }}>{protocol.protocol_name}</h2>
            <div style={{ color: CHROME, fontSize: 13, marginBottom: 12 }}>
              Counter-signer: <strong style={{ color: INK }}>{protocol.counter_signer}</strong> · Standard: {protocol.esign_standard}
            </div>
            {protocol.clauses.map(c => (
              <label key={c.id} style={{
                display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0",
                borderTop: `1px solid ${CHROME}22`, cursor: "pointer",
              }} data-testid={`clause-${c.id}`}>
                <input type="checkbox" checked={!!consent[c.id]}
                  onChange={e => setConsent({ ...consent, [c.id]: e.target.checked })}
                  data-testid={`clause-check-${c.id}`}
                  style={{ marginTop: 4 }} />
                <div>
                  <div style={{ fontWeight: 700 }}>{c.title}</div>
                  <div style={{ fontSize: 13, color: CHROME, lineHeight: 1.45, marginTop: 4 }}>{c.body}</div>
                </div>
              </label>
            ))}
          </div>

          <div style={card}>
            <h2 style={{ marginTop: 0 }}>Entity + Signer</h2>
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
              {[
                ["entity_legal_name", "Entity Legal Name", true],
                ["entity_ein_or_tax_id", "EIN / Tax ID (optional)", false],
                ["signer_full_name", "Signer Full Legal Name", true],
                ["signer_title", "Signer Title (optional)", false],
                ["signer_email", "Signer Email", true],
                ["signer_phone", "Signer Phone (optional)", false],
              ].map(([k, label, req]) => (
                <label key={k} style={{ display: "block" }}>
                  <div style={{ fontSize: 12, color: CHROME, marginBottom: 4 }}>
                    {label}{req && <span style={{ color: CYAN }}> *</span>}
                  </div>
                  <input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })}
                    style={input} data-testid={`activation-input-${k}`} />
                </label>
              ))}
            </div>
            <div style={{ marginTop: 20, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button style={btnGhost} onClick={() => setStep(2)} data-testid="activation-back-2">← Orientation</button>
              <button style={{
                ...btnPrimary,
                opacity: (allClausesAccepted && form.entity_legal_name && form.signer_full_name && form.signer_email && !submitting) ? 1 : 0.5,
                cursor: submitting ? "wait" : "pointer",
              }} disabled={!allClausesAccepted || submitting || !form.entity_legal_name || !form.signer_full_name || !form.signer_email}
                onClick={submit} data-testid="activation-execute-btn">
                {submitting ? "Executing…" : "Execute Mutual Service Activation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 4 · Success */}
      {step === 4 && result && (
        <div style={{ ...card, borderColor: CYAN, background: "rgba(0,207,207,0.08)" }} data-testid="activation-step-4">
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 8 }}>
            ✓ ACTIVATION EXECUTED · IMMUTABLE VAULT SEALED
          </div>
          <h2 style={{ marginTop: 0 }}>Welcome to the live digital platform.</h2>
          <div style={{ display: "grid", gap: 8, fontSize: 14, marginTop: 12 }}>
            <div><strong>Activation ID:</strong> <code style={{ color: CYAN }}>{result.activation_id}</code></div>
            <div><strong>Entity:</strong> {result.record.entity_legal_name}</div>
            <div><strong>Post-Activation Role:</strong> {result.record.post_activation_role} (Mandate Tier {result.record.mandate_tier})</div>
            <div><strong>Executed At (UTC):</strong> {result.record.executed_at}</div>
            <div style={{ wordBreak: "break-all" }}><strong>Vault Hash:</strong> <code style={{ color: CYAN, fontSize: 11 }}>{result.record.vault_hash}</code></div>
          </div>
          <div style={{ marginTop: 20, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <a style={btnPrimary} href={`${BACKEND_URL}${result.certificate_url}`}
              target="_blank" rel="noreferrer" data-testid="activation-cert-download">
              ⬇ Download Court-Grade Certificate
            </a>
            <Link to="/" style={btnGhost} data-testid="activation-home-link">Return to landing</Link>
          </div>
          <div style={{ marginTop: 16, fontSize: 12, color: CHROME }}>
            A signed copy is retained by both parties in the Moovin Forward LLC immutable audit vault.
            The SHA-256 record hash chains to the prior vault entry rendering the ledger tamper-evident.
          </div>
        </div>
      )}
    </div>
  );
}
